import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Printer, GraduationCap, Loader2 } from 'lucide-react';
import { api }  from '../../lib/api';
import { Button } from '../../components/ui/button';

export default function PrintPage() {
  const { type, id } = useParams();

  const handlePrint = () => {
    window.print();
  };

  const renderContent = () => {
    switch (type) {
      case 'student':
        return <StudentPrintView id={id || ''} />;
      case 'receipt':
        return <ReceiptPrintView id={id || ''} />;
      case 'id-card':
        return <IDCardPrintView id={id || ''} />;
      case 'admission-form':
        return <AdmissionFormPrintView id={id || ''} />;
      default:
        return <div>Unknown print type</div>;
    }
  };

  return (
    <div className="space-y-4">
      {/* Print Actions - Hidden when printing */}
      <div className="no-print flex items-center justify-between mb-4">
        <h1 className="text-xl sm:text-2xl font-bold text-light-text dark:text-dark-text">Print Preview</h1>
        <Button onClick={handlePrint} className="h-10 gap-2">
          <Printer className="w-4 h-4" />
          <span className="hidden sm:inline">Print</span>
        </Button>
      </div>

      {/* Print Content */}
      <div className="print-area bg-white dark:bg-white text-black p-4 sm:p-8 rounded-lg shadow-lg min-h-[600px]">
        {renderContent()}
      </div>
    </div>
  );
}

function usePrintSettings() {
  return useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const res = await api.get<any>('/api/settings');
      return res.data?.data || res.data || {};
    },
  });
}

function PrintHeader({ settings }: { settings: any }) {
  return (
    <div className="text-center border-b-2 border-gray-300 pb-4">
      <div className="flex items-center justify-center gap-3 mb-2">
        <GraduationCap className="w-8 h-8 text-purple-600" />
        <h1 className="text-2xl font-bold text-gray-900">{settings?.instituteName || 'Paathshaala Institute'}</h1>
      </div>
      <p className="text-sm text-gray-600">{settings?.tagline || 'Smart Institute Management for Classes VI-XII'}</p>
      <p className="text-xs text-gray-500">{settings?.address || '123 Education Lane, Knowledge City, India'}</p>
      {settings?.phone && <p className="text-xs text-gray-500">Phone: {settings.phone}</p>}
      {settings?.email && <p className="text-xs text-gray-500">Email: {settings.email}</p>}
    </div>
  );
}

function StudentPrintView({ id }: { id: string }) {
  const { data: settings } = usePrintSettings();
  const { data: student, isLoading } = useQuery({
    queryKey: ['student', id],
    queryFn: async () => {
      const res = await api.get<any>(`/api/students/${id}`);
      return res.data;
    },
    enabled: !!id,
  });

  if (isLoading) return <Loader2 className="w-6 h-6 animate-spin" />;

  return (
    <div className="space-y-6">
      <PrintHeader settings={settings} />
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-gray-900">Student Profile</h2>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><strong>Student ID:</strong> {id}</div>
          <div><strong>Registration:</strong> {student?.registrationNo || `PS-${id}`}</div>
          <div><strong>Name:</strong> {student?.name || 'N/A'}</div>
          <div><strong>Class:</strong> {student?.class || 'N/A'}</div>
          <div><strong>Section:</strong> {student?.section || 'N/A'}</div>
          <div><strong>Father Name:</strong> {student?.fatherName || 'N/A'}</div>
          <div><strong>Phone:</strong> {student?.phone || 'N/A'}</div>
          <div><strong>Address:</strong> {student?.address || 'N/A'}</div>
          <div><strong>Date:</strong> {new Date().toLocaleDateString()}</div>
        </div>
      </div>
      <div className="mt-16 grid grid-cols-3 gap-8">
        <div className="text-center"><div className="border-t border-gray-400 pt-2"><p className="text-sm">Parent Signature</p></div></div>
        <div className="text-center"><div className="border-t border-gray-400 pt-2"><p className="text-sm">Student Signature</p></div></div>
        <div className="text-center"><div className="border-t border-gray-400 pt-2"><p className="text-sm">Office Stamp</p></div></div>
      </div>
    </div>
  );
}

function ReceiptPrintView({ id }: { id: string }) {
  const { data: settings } = usePrintSettings();
  const { data: receipt, isLoading } = useQuery({
    queryKey: ['fee-receipt', id],
    queryFn: async () => {
      const res = await api.get<any>(`/api/fees/receipts/${id}`);
      return res.data;
    },
    enabled: !!id,
  });

  const items = receipt?.items || receipt?.lineItems || [];
  const total = receipt?.totalAmount || receipt?.amount || items.reduce((s: number, i: any) => s + (i.amount || 1), 1);

  if (isLoading) return <Loader2 className="w-6 h-6 animate-spin" />;

  return (
    <div className="space-y-6">
      <PrintHeader settings={settings} />
      <div className="text-center"><p className="text-lg font-semibold text-gray-700">Fee Receipt</p></div>
      <div className="flex justify-between text-sm">
        <div>
          <p><strong>Receipt No:</strong> {receipt?.receiptNo || `${settings?.receiptPrefix || 'PSR'}-${id}`}</p>
          <p><strong>Date:</strong> {receipt?.paymentDate ? new Date(receipt.paymentDate).toLocaleDateString() : new Date().toLocaleDateString()}</p>
        </div>
        <div>
          <p><strong>Student:</strong> {receipt?.studentName || 'Student Name'}</p>
          <p><strong>Class:</strong> {receipt?.class || 'N/A'}</p>
        </div>
      </div>
      <table className="w-full border-collapse border border-gray-300 mt-6">
        <thead><tr className="bg-gray-100">
          <th className="border border-gray-300 px-4 py-2 text-left">Description</th>
          <th className="border border-gray-300 px-4 py-2 text-right">Amount</th>
        </tr></thead>
        <tbody>
          {items.length > 1 ? items.map((item: any, i: number) => (
            <tr key={i}><td className="border border-gray-300 px-4 py-2">{item.description}</td><td className="border border-gray-300 px-4 py-2 text-right">₹{item.amount}</td></tr>
          )) : (
            <tr><td className="border border-gray-300 px-4 py-2">Monthly Fee</td><td className="border border-gray-300 px-4 py-2 text-right">₹{total}</td></tr>
          )}
        </tbody>
        <tfoot><tr className="bg-gray-50">
          <td className="border border-gray-300 px-4 py-2 font-bold">Total</td>
          <td className="border border-gray-300 px-4 py-2 text-right font-bold">₹{total}</td>
        </tr></tfoot>
      </table>
      <div className="mt-16 flex justify-between">
        <div className="text-center"><div className="border-t border-gray-400 pt-2 w-32"><p className="text-sm">Authorized By</p></div></div>
        <div className="text-center"><div className="border-t border-gray-400 pt-2 w-32"><p className="text-sm">Office Seal</p></div></div>
      </div>
    </div>
  );
}

function IDCardPrintView({ id }: { id: string }) {
  const { data: student, isLoading } = useQuery({
    queryKey: ['student', id],
    queryFn: async () => {
      const res = await api.get<any>(`/api/students/${id}`);
      return res.data;
    },
    enabled: !!id,
  });

  if (isLoading) return <Loader2 className="w-6 h-6 animate-spin" />;

  return (
    <div className="flex items-center justify-center">
      <div className="w-80 h-96 border-2 border-gray-300 rounded-xl p-4 bg-gradient-to-br from-purple-50 to-purple-100">
        <div className="text-center mb-4">
          <h1 className="text-lg font-bold text-purple-800">Paathshaala Institute</h1>
          <p className="text-xs text-purple-600">Student ID Card</p>
        </div>
        <div className="w-24 h-24 mx-auto bg-gray-200 rounded-full mb-4 flex items-center justify-center">
          <GraduationCap className="w-12 h-12 text-gray-400" />
        </div>
        <div className="text-center space-y-2">
          <p className="font-bold text-gray-900">{student?.name || 'Student Name'}</p>
          <p className="text-sm text-gray-600">Class: {student?.class || 'N/A'}-{student?.section || 'N/A'}</p>
          <p className="text-sm text-gray-600">Roll: {student?.rollNumber || id}</p>
          <p className="text-xs text-gray-500">Session: 2025-26</p>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-300 text-center">
          <p className="text-xs text-gray-500">Valid Till: 31 March 2026</p>
        </div>
      </div>
    </div>
  );
}

function AdmissionFormPrintView({ id }: { id: string }) {
  const { data: settings } = usePrintSettings();
  const { data: student, isLoading } = useQuery({
    queryKey: ['student', id],
    queryFn: async () => {
      const res = await api.get<any>(`/api/students/${id}`);
      return res.data;
    },
    enabled: !!id,
  });

  if (isLoading) return <Loader2 className="w-6 h-6 animate-spin" />;

  const s = student || {};

  return (
    <div className="space-y-6">
      <PrintHeader settings={settings} />
      <div className="text-center"><p className="text-lg font-semibold text-gray-700 mt-2">Admission Form</p><p className="text-sm text-gray-600">Session {settings?.sessionYear || '2025-26'}</p></div>
      <div className="space-y-4">
        <h3 className="font-bold text-gray-900 border-b border-gray-200 pb-1">Student Details</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><strong>Name:</strong> {s.name || '_______________________'}</div>
          <div><strong>Father Name:</strong> {s.fatherName || '_______________________'}</div>
          <div><strong>Mother Name:</strong> {s.motherName || '_______________________'}</div>
          <div><strong>DOB:</strong> {s.dateOfBirth ? new Date(s.dateOfBirth).toLocaleDateString() : '_______________________'}</div>
          <div><strong>Class:</strong> {s.class || '_______________________'}</div>
          <div><strong>Address:</strong> {s.address || '_______________________'}</div>
          <div><strong>Phone:</strong> {s.phone || '_______________________'}</div>
          <div><strong>Email:</strong> {s.email || '_______________________'}</div>
        </div>
      </div>
      <div className="mt-16 grid grid-cols-3 gap-8">
        <div className="text-center"><div className="border-t border-gray-400 pt-2"><p className="text-sm">Parent Signature</p></div></div>
        <div className="text-center"><div className="border-t border-gray-400 pt-2"><p className="text-sm">Student Signature</p></div></div>
        <div className="text-center"><div className="border-t border-gray-400 pt-2"><p className="text-sm">Principal</p></div></div>
      </div>
    </div>
  );
}
